'use client';

import './project-mobile-detail.scss';
import { WarningOutlined } from "@ant-design/icons";
import { ProjectModel } from "@/lib/models/Project";
import ImageElement from '@/components/elements/ImageElement';
import MobileFooterButtons from '@/components/Mobile/FooterMobile/MobileFooterButtons';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { RouterName } from '@/lib/enum/RouterEnum';
import { setNavState } from '@/lib/utils/navState';
import { PAGE_MOBILE } from '@/lib/constants';
import { useTransitionExit } from '@/components/Mobile/Transition/SwipeTransition';
import TransitionContext from '@/components/Mobile/Transition/TransitionContext';
import VideoElement from '@/components/elements/VideoElement';

interface Props {
  project: ProjectModel;
  projects: ProjectModel[];
}

const ProjectMobileDetail = ({ project, projects }: Props) => {
  const swipeDownLength = 150;
  const pathname = usePathname();
  const { markRouteReady } = useContext(TransitionContext);

  useEffect(() => {
    markRouteReady(pathname);
  }, [pathname, markRouteReady]);

  const { prevProjectUrl, nextProjectUrl } = useMemo(() => {
    const charExits = /\W/g;
    const currentIndex = projects.findIndex(item => item.id === project.id);
    const prevIndex = currentIndex - 1;
    const nextIndex = currentIndex + 1;
    return {
      prevProjectUrl: prevIndex >= 0
        ? `${RouterName.projectDetail}/${projects[prevIndex].title.replaceAll(' ', '_').replace(charExits, '')}`
        : '',
      nextProjectUrl: nextIndex < projects.length
        ? `${RouterName.projectDetail}/${projects[nextIndex].title.replaceAll(' ', '_').replace(charExits, '')}`
        : '',
    };
  }, [project, projects]);

  const exit = useTransitionExit();

  // Handle swipe navigation
  const touchStartYRef = useRef(0);
  const touchEndYRef = useRef(0);
  const projectRef = useRef<HTMLDivElement>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartYRef.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    touchEndYRef.current = e.changedTouches[0].clientY;
    handleSwipe();
  };

  const handleSwipe = () => {
    const swipeDistance = touchStartYRef.current - touchEndYRef.current;
    const body = projectRef.current;
    if (body) {
      const atTopOfScroll = body.scrollTop <= 0;
      if (atTopOfScroll && swipeDistance < -swipeDownLength) {
        goBack();
      }
    }
  };

  const goBack = () => {
    const projectIdx = projects.findIndex(p => p.id === project.id);
    if (projectIdx > -1) setNavState(projectIdx + PAGE_MOBILE.PROJECTS);
    exit(`${RouterName.dashboard}`);
  };

  const _renderImage = (link: string, index: number) => {
    return (
      <div className="project-image-container" key={index}>
        <ImageElement
          src={link}
          className='project-image'
          alt={""}
          scroller=".project-mobile-detail"
          priority={index === 0}
        />
      </div>
    )
  };

  const _renderVideo = (link: string, index: number) => {
    return (
      <div className='project-image-container' key={index}>
        <VideoElement
          src={link}
          className='project-image'
          scroller=".project-mobile-detail"
        />
      </div>
    );
  };

  const handlePrevStep = () => {
    exit(prevProjectUrl);
  };

  const handleNextStep = () => {
    exit(nextProjectUrl);
  };

  return (
    <>
      <div className={'project-mobile-detail'}
        ref={projectRef}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className={'project-description-container'} id={'header-project-detail-mobile'}>
          <p className={'text-experiment'}>EXPERIMENT</p>
          <p className={'text-name-project'}>{project?.detailTitle}</p>
          <p className={'text-description-project'}>{project?.detailDescription}</p>
        </div>
        {
          // Render project images and videos...
          project && project.detailImages && project.detailImages.map((item, index) => {
            if (item.includes('.mp4') || item.includes('.webm')) {
              return _renderVideo(item, index)
            } else {
              return _renderImage(item, index)
            }
          })
        }

        {
        // add webm files into public/assets/ if you'd like to test
        /* { _renderVideo('/assets/PUBG_THUMB.webm', 42) }
        { _renderVideo('/assets/PUBG_01.webm', 43) }
        { _renderVideo('/assets/PUBG_02.webm', 44) }
        { _renderVideo('/assets/PUBG_04.webm', 45) } */}

        <div className='end-project-item'>
          <WarningOutlined className='icon-warning' />
          End of Experiment
        </div>
      </div>
      <MobileFooterButtons
        onPrev={handlePrevStep}
        onNext={handleNextStep}
        prevDisabled={!prevProjectUrl}
        nextDisabled={!nextProjectUrl}
      />
    </>
  )
};

export default ProjectMobileDetail;
